import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import React, { useEffect, useMemo, useState } from 'react';
import { FlatList, Pressable, RefreshControl, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, {
  FadeInDown,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';

import { MoreMenu } from '@/components/navigation/MoreMenu';
import { CompetitionGroup, type FixtureGroup } from '@/components/matches/CompetitionGroup';
import { DateStrip } from '@/components/matches/DateStrip';
import { DiscoverRail } from '@/components/matches/DiscoverRail';
import { FixtureRow } from '@/components/matches/FixtureRow';
import { HeroMatchCard } from '@/components/matches/HeroMatchCard';
import { AppText } from '@/components/ui/AppText';
import { BrandWordmark } from '@/components/ui/BrandWordmark';
import { ClockIcon, MenuIcon, SearchIcon, StarIcon } from '@/components/ui/icons';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { isSupabaseConfigured } from '@/config';
import { useThemePalette } from '@/config/theme';
import { useFavorites } from '@/features/favorites/hooks';
import {
  useFixturesForDay,
  useFixturesRealtime,
  useLiveFixtures,
  useStandings,
} from '@/features/matches/hooks';
import { useProfile } from '@/features/profile/hooks';
import { predictMatch } from '@/features/predictions/model';
import type { FixtureWithRefs } from '@/lib/data/provider';
import { useRefresh } from '@/lib/data/useRefresh';
import { formatNumber, isSameDay } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import type { Tables } from '@/lib/supabase/types';

const brandLogo = require('../../../assets/images/brand-mark.png');

/** A softly pulsing live dot for the "LIVE NOW" header. */
function LivePulse() {
  const palette = useThemePalette();
  const reduce = useReducedMotion();
  const pulse = useSharedValue(reduce ? 0.6 : 0);
  useEffect(() => {
    if (reduce) return;
    pulse.value = withRepeat(withTiming(1, { duration: 900 }), -1, true);
  }, [reduce, pulse]);
  const style = useAnimatedStyle(() => ({
    opacity: 0.4 + pulse.value * 0.6,
    transform: [{ scale: 0.85 + pulse.value * 0.35 }],
  }));
  return (
    <Animated.View style={[{ width: 9, height: 9, borderRadius: 5, backgroundColor: palette.volt }, style]} />
  );
}

function HeaderIcon({
  label,
  onPress,
  children,
}: {
  label: string;
  onPress: () => void;
  children: React.ReactNode;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      className="h-9 w-9 items-center justify-center rounded-full border border-line bg-surface active:bg-surface-2"
    >
      {children}
    </Pressable>
  );
}

type HomeFilter = 'all' | 'live' | 'following';

function FilterChip({
  label,
  badge,
  active,
  onPress,
}: {
  label: string;
  badge?: string;
  active: boolean;
  onPress: () => void;
}) {
  const palette = useThemePalette();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      onPress={onPress}
      className={`flex-row items-center gap-1.5 rounded-full px-3.5 py-1.5 ${
        active ? 'bg-volt' : 'border border-line bg-surface active:bg-surface-2'
      }`}
    >
      <AppText variant="micro" weight="bold" style={{ color: active ? palette.onVolt : palette.soft }}>
        {label}
      </AppText>
      {badge ? (
        <View
          className={`min-w-[16px] items-center rounded-full px-1 ${active ? 'bg-black/20' : 'bg-volt/20'}`}
        >
          <AppText
            variant="micro"
            weight="bold"
            tabular
            style={{ color: active ? palette.onVolt : palette.voltBright }}
          >
            {badge}
          </AppText>
        </View>
      ) : null}
    </Pressable>
  );
}

/** Pick today's headline: a live match first, else the next one to kick off. */
function pickHero(fixtures: FixtureWithRefs[]): FixtureWithRefs | null {
  const live = fixtures.find((fixture) => fixture.status === 'live');
  if (live) return live;
  const upcoming = fixtures.filter((fixture) => fixture.status === 'scheduled');
  return upcoming[0] ?? fixtures[0] ?? null;
}

export default function MatchesScreen() {
  const palette = useThemePalette();
  const router = useRouter();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [moreOpen, setMoreOpen] = useState(false);
  const fixturesQuery = useFixturesForDay(selectedDate);
  // Currently-live matches, fetched globally (any calendar day) so a game that
  // kicked off late and crossed midnight never vanishes from the home.
  const liveQuery = useLiveFixtures();
  const reducedMotion = useReducedMotion();
  useFixturesRealtime();

  const fixtures = useMemo(() => fixturesQuery.data ?? [], [fixturesQuery.data]);
  const allLive = useMemo(() => liveQuery.data ?? [], [liveQuery.data]);
  const liveCount = allLive.length;
  const liveBadge = liveCount > 0 ? formatNumber(liveCount, numeralOptions) : undefined;
  const isToday = isSameDay(selectedDate, new Date());

  const favoritesQuery = useFavorites();
  const profileQuery = useProfile();
  const { refreshing, onRefresh } = useRefresh([
    () => fixturesQuery.refetch(),
    () => favoritesQuery.refetch(),
  ]);
  const followSets = useMemo(() => {
    const teams = new Set<string>();
    const competitions = new Set<string>();
    for (const favorite of favoritesQuery.data ?? []) {
      if (favorite.entity_type === 'team') teams.add(favorite.entity_id);
      else if (favorite.entity_type === 'competition') competitions.add(favorite.entity_id);
    }
    return { teams, competitions };
  }, [favoritesQuery.data]);

  // Teams to spotlight in the list: everything the user follows PLUS their single
  // favourite club - their fixtures get a heart frame so they stand out.
  const followedTeamIds = useMemo(() => {
    const ids = new Set(followSets.teams);
    if (profileQuery.data?.favorite_team_id) ids.add(profileQuery.data.favorite_team_id);
    return ids;
  }, [followSets.teams, profileQuery.data]);

  const [filter, setFilter] = useState<HomeFilter>('all');
  const matchesFollowing = (fixture: FixtureWithRefs): boolean =>
    (fixture.home_team != null && followSets.teams.has(fixture.home_team.id)) ||
    (fixture.away_team != null && followSets.teams.has(fixture.away_team.id)) ||
    (fixture.competition != null && followSets.competitions.has(fixture.competition.id));

  const visibleFixtures = useMemo(() => {
    // The Live filter shows EVERY live match (any day), not just the viewed day's.
    if (filter === 'live') return allLive;
    if (filter === 'following') return fixtures.filter(matchesFollowing);
    return fixtures;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixtures, filter, followSets, allLive]);

  // Live matches are pinned to the top in their own "LIVE NOW" strip - sourced
  // from the GLOBAL live query so a late game that crossed midnight still shows
  // on "today". Skipped on the Live filter (the whole list is already live).
  // Excluded from the hero/groups below so they aren't shown twice.
  const liveFixtures = useMemo(() => {
    if (filter === 'live') return [];
    if (filter === 'following') return allLive.filter(matchesFollowing);
    return allLive;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLive, filter, followSets]);
  const restFixtures = useMemo(() => {
    if (liveFixtures.length === 0) return visibleFixtures;
    const liveIds = new Set(liveFixtures.map((fixture) => fixture.id));
    return visibleFixtures.filter((fixture) => !liveIds.has(fixture.id));
  }, [visibleFixtures, liveFixtures]);

  const isFollowedFixture = (fixture: FixtureWithRefs): boolean =>
    (fixture.home_team_id != null && followedTeamIds.has(fixture.home_team_id)) ||
    (fixture.away_team_id != null && followedTeamIds.has(fixture.away_team_id));

  // The hero is the single headline card. Prefer a LIVE match so the biggest
  // card is always the most important one - never an upcoming match sitting
  // above (and larger than) a game that's actually in play.
  const hero = useMemo(
    () =>
      filter === 'all' && isToday ? pickHero([...liveFixtures, ...restFixtures]) : null,
    [filter, isToday, liveFixtures, restFixtures],
  );
  // The LIVE NOW strip shows the OTHER live games (the hero already features one).
  const stripLive = useMemo(
    () => liveFixtures.filter((fixture) => fixture.id !== hero?.id),
    [liveFixtures, hero],
  );
  // Group the remaining matches by competition (365Scores-style league sections).
  // Within a group: live → scheduled → finished → postponed, then by kickoff.
  // Groups are ordered live-first, then by their earliest kickoff.
  const groups = useMemo<FixtureGroup[]>(() => {
    const order: Record<string, number> = { live: 0, scheduled: 1, finished: 2, postponed: 3 };
    const sorted = restFixtures
      .filter((fixture) => fixture.id !== hero?.id)
      .sort(
        (a, b) =>
          (order[a.status] ?? 9) - (order[b.status] ?? 9) ||
          a.kickoff_at.localeCompare(b.kickoff_at),
      );
    const map = new Map<string, FixtureGroup>();
    for (const fixture of sorted) {
      const key = fixture.competition?.id ?? 'none';
      let group = map.get(key);
      if (!group) {
        group = { key, competition: fixture.competition, fixtures: [], hasLive: false };
        map.set(key, group);
      }
      group.fixtures.push(fixture);
      if (fixture.status === 'live') group.hasLive = true;
    }
    const earliest = (group: FixtureGroup) =>
      group.fixtures.reduce(
        (min, fixture) => (fixture.kickoff_at < min ? fixture.kickoff_at : min),
        group.fixtures[0]?.kickoff_at ?? '',
      );
    return Array.from(map.values()).sort((a, b) => {
      if (a.hasLive !== b.hasLive) return a.hasLive ? -1 : 1;
      return earliest(a).localeCompare(earliest(b));
    });
  }, [restFixtures, hero]);

  const heroStandings = useStandings(hero?.competition_id, hero?.season);
  const heroData = useMemo(() => {
    if (!hero) return null;
    const rows = heroStandings.data ?? [];
    const byTeam = new Map<string, Tables<'standings'>>(rows.map((row) => [row.team_id, row]));
    const homeRow = hero.home_team ? byTeam.get(hero.home_team.id) : undefined;
    const awayRow = hero.away_team ? byTeam.get(hero.away_team.id) : undefined;
    if (!homeRow && !awayRow) return null;
    const toInput = (row: Tables<'standings'> | undefined) =>
      row ? { played: row.played, points: row.points, gd: row.gd, form: row.form } : null;
    return {
      probabilities: predictMatch(toInput(homeRow), toInput(awayRow)),
    };
  }, [hero, heroStandings.data]);

  return (
    <Screen>
      <MoreMenu visible={moreOpen} onClose={() => setMoreOpen(false)} />
      {/* Volt header: logo · live pill · following · search · more */}
      <View className="flex-row items-center gap-2.5 px-4 pb-3 pt-3">
        <Image
          source={brandLogo}
          style={{ height: 38, width: 38 }}
          contentFit="contain"
          accessibilityLabel={t('common.appName')}
        />
        <BrandWordmark size={22} />
        <View className="flex-1" />
        <HeaderIcon label={t('today.title')} onPress={() => router.push('/today' as never)}>
          <ClockIcon size={16} color={palette.soft} />
        </HeaderIcon>
        <HeaderIcon label={t('following.title')} onPress={() => router.push('/following')}>
          <StarIcon size={16} color={palette.soft} />
        </HeaderIcon>
        <HeaderIcon label={t('tabs.search')} onPress={() => router.push('/search')}>
          <SearchIcon size={16} color={palette.soft} />
        </HeaderIcon>
        <HeaderIcon label={t('more.title')} onPress={() => setMoreOpen(true)}>
          <MenuIcon size={16} color={palette.soft} />
        </HeaderIcon>
      </View>

      <View className="pb-2">
        <DateStrip selected={selectedDate} onSelect={setSelectedDate} />
      </View>

      {isSupabaseConfigured &&
      !fixturesQuery.isPending &&
      !fixturesQuery.isError &&
      (fixtures.length > 0 || liveFixtures.length > 0) ? (
        <View className="flex-row gap-2 px-4 pb-3">
          <FilterChip
            label={t('matches.filterAll')}
            active={filter === 'all'}
            onPress={() => setFilter('all')}
          />
          <FilterChip
            label={t('matches.filterLive')}
            badge={liveBadge}
            active={filter === 'live'}
            onPress={() => setFilter('live')}
          />
          <FilterChip
            label={t('matches.filterFollowing')}
            active={filter === 'following'}
            onPress={() => setFilter('following')}
          />
        </View>
      ) : null}

      {!isSupabaseConfigured ? (
        <EmptyState
          icon="settings-outline"
          title={t('common.notConfiguredTitle')}
          body={t('common.notConfiguredBody')}
        />
      ) : fixturesQuery.isPending ? (
        <View className="gap-3 px-4 pt-2">
          <Skeleton className="h-44 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </View>
      ) : fixturesQuery.isError ? (
        <ErrorState error={fixturesQuery.error} onRetry={() => fixturesQuery.refetch()} />
      ) : fixtures.length === 0 && liveFixtures.length === 0 ? (
        <EmptyState
          icon="calendar-outline"
          title={t('matches.noMatchesTitle')}
          body={t('matches.noMatchesBody')}
        />
      ) : visibleFixtures.length === 0 && liveFixtures.length === 0 ? (
        <EmptyState
          icon={filter === 'following' ? 'star-outline' : 'flash-outline'}
          title={filter === 'following' ? t('matches.followingEmptyTitle') : t('matches.noLiveTitle')}
          body={filter === 'following' ? t('matches.followingEmptyBody') : t('matches.noLiveBody')}
        />
      ) : (
        <FlatList
          data={groups}
          keyExtractor={(group) => group.key}
          contentContainerStyle={{ paddingTop: 4, paddingBottom: 24 }}
          showsVerticalScrollIndicator={false}
          initialNumToRender={6}
          maxToRenderPerBatch={6}
          windowSize={9}
          removeClippedSubviews
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              tintColor={palette.volt}
              colors={[palette.volt]}
              progressBackgroundColor={palette.surface}
            />
          }
          ListHeaderComponent={
            // Discover rail first (surfaces news / groups / sports / history),
            // then the featured match (the biggest card), the live games, and
            // the competition divider. The groups below are virtualized.
            <>
              {filter === 'all' ? <DiscoverRail /> : null}

              {hero ? (
                <Animated.View
                  entering={reducedMotion ? undefined : FadeInDown.springify()}
                  className="pb-5"
                >
                  <View className="mx-4 mb-2.5 flex-row items-center gap-2">
                    <Ionicons name="flame" size={14} color={palette.volt} />
                    <AppText
                      variant="caption"
                      weight="bold"
                      className="uppercase tracking-widest text-ink"
                    >
                      {t('matches.featuredMatch')}
                    </AppText>
                    <View className="h-px flex-1 bg-line" />
                  </View>
                  <HeroMatchCard
                    fixture={hero}
                    probabilities={hero.status === 'scheduled' ? (heroData?.probabilities ?? null) : null}
                  />
                </Animated.View>
              ) : null}

              {stripLive.length > 0 ? (
                <Animated.View
                  entering={reducedMotion ? undefined : FadeInDown.springify()}
                  className="pb-4"
                >
                  <View className="mx-4 mb-2 flex-row items-center gap-2">
                    <LivePulse />
                    <AppText variant="caption" weight="bold" className="uppercase tracking-widest text-volt-bright">
                      {t('matches.liveNow')}
                    </AppText>
                    <View className="rounded-full bg-volt-glass px-2 py-0.5">
                      <AppText variant="micro" weight="bold" tabular className="text-volt-bright">
                        {formatNumber(stripLive.length, numeralOptions)}
                      </AppText>
                    </View>
                  </View>
                  {stripLive.map((fixture) => (
                    <FixtureRow key={fixture.id} fixture={fixture} followed={isFollowedFixture(fixture)} />
                  ))}
                </Animated.View>
              ) : null}

              {groups.length > 0 ? (
                <View className="mx-4 mb-2 flex-row items-center gap-2">
                  <AppText
                    variant="micro"
                    weight="bold"
                    className="uppercase tracking-widest text-muted"
                  >
                    {t('matches.competitions')}
                  </AppText>
                  <View className="h-px flex-1 bg-line" />
                </View>
              ) : null}
            </>
          }
          renderItem={({ item: group, index }) => (
            <Animated.View
              entering={
                reducedMotion ? undefined : FadeInDown.delay(Math.min(index, 4) * 50).springify()
              }
            >
              <CompetitionGroup group={group} followedTeamIds={followedTeamIds} />
            </Animated.View>
          )}
        />
      )}
    </Screen>
  );
}
